import { AxiosHeaders, type AxiosResponse } from 'axios';

import { apiClient } from './client';

export type ShopifyGraphqlProxyResult = {
  ok: boolean;
  status: number;
  statusText: string;
  bodyText: string;
  headers: Record<string, string>;
};

function responseHeadersToRecord (
  headers: AxiosResponse['headers'],
): Record<string, string> {
  const json = AxiosHeaders.from(headers as AxiosHeaders).toJSON(true);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(json)) {
    if (v != null) out[k] = String(v);
  }
  return out;
}

/**
 * Proxies Shopify Admin GraphQL through the internal API (same path GraphiQL uses).
 * Uses {@link apiClient} for auth headers. Status **401** is not treated as success so
 * axios rejects and the response interceptor in `client.ts` runs (session clear + redirect).
 * Other HTTP statuses resolve so GraphiQL can show proxy/Shopify error bodies.
 */
export async function postShopifyGraphql (
  shopId: number | string,
  body: string,
  options?: {
    headers?: Record<string, string>;
    signal?: AbortSignal;
  },
): Promise<ShopifyGraphqlProxyResult> {
  const response = await apiClient.post<string>(
    `/${shopId}/shop/shopify-graphql`,
    body,
    {
      responseType: 'text',
      // Reject on 401 only so global 401 handling runs; still accept other statuses for GraphiQL.
      validateStatus: (status) => status !== 401,
      headers: {
        ...options?.headers,
      },
      signal: options?.signal,
    },
  );

  return {
    ok: response.status >= 200 && response.status < 300,
    status: response.status,
    statusText: response.statusText,
    bodyText: response.data,
    headers: responseHeadersToRecord(response.headers),
  };
}
